import { Trans } from "@lingui/react/macro"
import { Text } from "@mantine/core"
import {
  Background,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  type NodeTypes,
  Position,
  ReactFlow,
  type ReactFlowProps,
} from "@xyflow/react"
import { useCallback, useMemo } from "react"

import { layoutPipelineNodes } from "../../lib/pipeline-layout"
import { formatStepDuration } from "./helpers"
import type { IPipelineGraphNode, IProps, IStepRunStatus, TStepCategory, TStepStatus } from "./interfaces"

import "@xyflow/react/dist/style.css"
import classes from "./styles.module.css"

/** Categories shown in the topology-mode legend, in render order. */
const LEGEND_CATEGORIES: readonly TStepCategory[] = [
  "speaker",
  "summary",
  "analysis",
  "images",
  "reports",
  "persistence",
  "export",
  "translation",
  "audio",
]

const CATEGORY_LABELS: Record<TStepCategory, React.ReactNode> = {
  speaker: <Trans>Speaker</Trans>,
  summary: <Trans>Summary</Trans>,
  analysis: <Trans>Analysis</Trans>,
  images: <Trans>Images</Trans>,
  reports: <Trans>Reports</Trans>,
  persistence: <Trans>Persistence</Trans>,
  export: <Trans>Export</Trans>,
  translation: <Trans>Translation</Trans>,
  audio: <Trans>Audio</Trans>,
  other: <Trans>Other</Trans>,
}

const STATUS_LEGEND: readonly TStepStatus[] = ["done", "running", "queued", "failed", "skipped"]

const STATUS_LABELS: Record<TStepStatus, React.ReactNode> = {
  done: <Trans>Done</Trans>,
  running: <Trans>Running</Trans>,
  queued: <Trans>Queued</Trans>,
  failed: <Trans>Failed</Trans>,
  skipped: <Trans>Skipped</Trans>,
}

/** Data carried on each React Flow node — the display fields of a pipeline step. */
interface IStepNodeData extends Record<string, unknown> {
  name: string
  ref: string
  category: TStepCategory
  /** Present only in run mode when this node has a status entry. */
  runStatus?: IStepRunStatus
  selected?: boolean
}

type TStepNode = Node<IStepNodeData, "step">

/**
 * Read-only step node. Connection handles are non-interactive (the whole
 * graph is pan/zoom only). Category is a secondary top-hairline cue; when a
 * run status exists, `data-status` drives fill/border and duration/bottleneck
 * chrome appears.
 */
function StepNode({ data }: NodeProps<TStepNode>): React.JSX.Element {
  const status = data.runStatus
  return (
    <div
      className={classes.node}
      data-category={data.category}
      data-status={status?.status}
      data-bottleneck={status?.isBottleneck ? "true" : undefined}
      data-selected={data.selected ? "true" : undefined}
      data-testid={`pipeline-node-${data.ref}`}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <div className={classes.nodeHeader}>
        <span className={classes.nodeName}>{data.name}</span>
        {status?.isBottleneck ? (
          <span className={classes.bottleneckBadge}>
            <Trans>Slow</Trans>
          </span>
        ) : null}
      </div>
      <span className={classes.nodeMeta}>{data.ref}</span>
      {status ? <span className={classes.nodeDuration}>{formatStepDuration(status.durationMs)}</span> : null}
      <Handle type="source" position={Position.Right} isConnectable={false} />
    </div>
  )
}

const nodeTypes: NodeTypes = { step: StepNode }

/**
 * Dumb, read-only DAG renderer for the finalize pipeline. Takes already-fetched
 * nodes/edges + their topological wave count and lays them out left-to-right.
 * No data fetching, no app hooks — pan/zoom only.
 *
 * Optional `runStatus` switches the graph into run-overlay mode (status legend,
 * duration/bottleneck chrome). Without it the component matches pre-Phase-2b
 * topology-only behaviour.
 */
export function PipelineGraph({
  nodes,
  edges,
  waveCount: _waveCount,
  runStatus,
  selectedNodeId = null,
  onNodeSelect,
}: IProps): React.JSX.Element {
  const isRunMode = runStatus !== undefined

  /**
   * Topology identity only — status/duration overlays must not remount React Flow
   * (that would re-run fitView and wipe pan/zoom). Remount when the node/edge SET
   * changes (e.g. different template). Layout itself is deterministic from waves.
   */
  const structureKey = useMemo(
    () => `${nodes.map((n) => n.id).join("\0")}\n${edges.map((e) => `${e.source}>${e.target}`).join("\0")}`,
    [nodes, edges],
  )

  const flowNodes: TStepNode[] = useMemo(() => {
    // Positions are pure functions of wave layout — same node set → same coords.
    // Status/selection live only in `data` / `selected`, so poll refetches recolor
    // in place without shifting the graph.
    const positions = new Map(layoutPipelineNodes(nodes).map((p) => [p.id, p]))
    return nodes.map((node: IPipelineGraphNode) => {
      const pos = positions.get(node.id)
      const status = runStatus?.[node.id]
      return {
        id: node.id,
        type: "step" as const,
        position: { x: pos?.x ?? 0, y: pos?.y ?? 0 },
        data: {
          name: node.name,
          ref: node.ref,
          category: node.category,
          runStatus: status,
          selected: selectedNodeId === node.id,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        draggable: false,
        connectable: false,
        deletable: false,
        selectable: Boolean(onNodeSelect),
        // Controlled from parent (`selectedNodeId`) so selection survives polls.
        selected: selectedNodeId === node.id,
      }
    })
  }, [nodes, runStatus, selectedNodeId, onNodeSelect])

  const flowEdges: Edge[] = useMemo(
    () =>
      edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        animated: edge.optional,
        style: edge.optional ? { strokeDasharray: "4 4" } : undefined,
        deletable: false,
        selectable: false,
      })),
    [edges],
  )

  const usedCategories = useMemo(() => {
    const present = new Set(nodes.map((n) => n.category))
    return LEGEND_CATEGORIES.filter((c) => present.has(c))
  }, [nodes])

  const handleNodeClick = useCallback<NonNullable<ReactFlowProps["onNodeClick"]>>(
    (_event, node) => {
      onNodeSelect?.(node.id)
    },
    [onNodeSelect],
  )

  const handlePaneClick = useCallback((): void => {
    onNodeSelect?.(null)
  }, [onNodeSelect])

  return (
    <>
      <div className={`${classes.root} lex-surface-card`} data-testid="pipeline-graph">
        {/*
          key=structureKey remounts (and fitViews) only when the node/edge SET
          changes. Status-only poll updates keep the same key → pan/zoom stick.
        */}
        <ReactFlow
          key={structureKey}
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15, minZoom: 0.7, maxZoom: 1 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={Boolean(onNodeSelect)}
          edgesFocusable={false}
          onNodeClick={onNodeSelect ? handleNodeClick : undefined}
          onPaneClick={onNodeSelect ? handlePaneClick : undefined}
          proOptions={{ hideAttribution: true }}
          minZoom={0.7}
          maxZoom={1.5}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      <div className={classes.legend} data-testid={isRunMode ? "pipeline-status-legend" : "pipeline-category-legend"}>
        {isRunMode ? (
          <>
            {STATUS_LEGEND.map((status) => (
              <span key={status} className={classes.legendItem}>
                <span className={classes.legendSwatch} data-status={status} />
                <Text component="span" size="xs" c="dimmed">
                  {STATUS_LABELS[status]}
                </Text>
              </span>
            ))}
            <span className={classes.legendItem}>
              <span className={classes.legendSwatch} data-bottleneck="true" />
              <Text component="span" size="xs" c="dimmed">
                <Trans>Bottleneck</Trans>
              </Text>
            </span>
          </>
        ) : (
          usedCategories.map((category) => (
            <span key={category} className={classes.legendItem}>
              <span className={classes.legendSwatch} data-category={category} />
              <Text component="span" size="xs" c="dimmed">
                {CATEGORY_LABELS[category]}
              </Text>
            </span>
          ))
        )}
      </div>
    </>
  )
}
